import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { cn } from '@/lib/cn'
import { formatCurrency } from '@/lib/format'
import { queryKeys } from '@/lib/queryKeys'
import { routes } from '@/routes'
import { BackLink } from '@/components/layout/BackLink'
import { AddPurchaseButton } from '@/components/product/AddPurchaseButton'
import { Price } from '@/components/product/Price'
import { ProductBadges } from '@/components/product/ProductBadges'
import { ShareButton } from '@/components/product/ShareButton'
import { ButtonLink } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Rating } from '@/components/ui/Rating'
import { Spinner } from '@/components/ui/Spinner'

export function ProductPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const [activeImage, setActiveImage] = useState(0)

  const { data: product, isLoading, isError } = useQuery({
    queryKey: queryKeys.product(slug),
    queryFn: () => api.catalog.getProduct(slug),
  })

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size={30} className="text-brand" />
      </div>
    )
  }

  if (isError || !product) {
    return (
      <div className="mx-auto max-w-[1200px] px-4 py-8">
        <BackLink />
        <Card className="mt-4 px-6 py-16 text-center">
          <p className="text-lg font-semibold text-ink">Produto não encontrado</p>
          <p className="mt-1.5 text-sm text-ink-soft">
            O anúncio pode ter saído do ar ou o endereço está incorreto.
          </p>
        </Card>
      </div>
    )
  }

  const path = routes.product(product.slug)

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-5">
      <BackLink className="mb-3" />

      <Card className="p-4 sm:p-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Galeria + informações principais */}
          <div className="grid gap-6 sm:grid-cols-[64px_minmax(0,1fr)] lg:grid-cols-[64px_minmax(0,1fr)]">
            <Thumbnails
              images={product.images}
              title={product.title}
              active={activeImage}
              onSelect={setActiveImage}
            />

            <div className="group relative">
              <div className="absolute right-3 top-3 z-10">
                <AddPurchaseButton productId={product.id} productTitle={product.title} />
              </div>
              <img
                src={product.images[activeImage]}
                alt={product.title}
                className="aspect-square w-full rounded object-contain"
              />
            </div>
          </div>

          {/* Coluna de conteúdo — em telas largas fica ao lado da galeria. */}
          <div className="flex flex-col gap-5 lg:col-start-2 lg:row-start-1">
            <div>
              <ProductBadges badges={product.badges} className="mb-2" />
              <h1 className="text-[19px] font-semibold leading-tight text-ink lg:text-2xl">
                {product.title}
              </h1>
              {product.rating != null && (
                <Rating
                  value={product.rating}
                  count={product.ratingCount}
                  showValue
                  className="mt-2"
                />
              )}
            </div>

            <Price
              value={product.price}
              originalValue={product.originalPrice}
              discountPercent={product.discountPercent}
              size="xl"
            />

            {product.highlights.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-ink">Sobre o item:</h2>
                <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-[13px] text-ink-soft">
                  {product.highlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-3 rounded border border-line p-4">
              {product.deliveryDescription && (
                <div>
                  <p className="text-[13px] text-ink-soft">Descrição da entrega:</p>
                  <p className="mt-1 text-[13px] font-medium text-brand">
                    {product.deliveryDescription}
                  </p>
                </div>
              )}

              {product.availableQuantity != null && (
                <p className="text-[13px] text-ink-soft">
                  Quant. disponível: {product.availableQuantity}
                </p>
              )}

              <ButtonLink to={product.externalUrl} external variant="accent" size="lg" block>
                Comprar agora
              </ButtonLink>

              <p className="text-center text-[11px] leading-snug text-ink-faint">
                Você será levado ao site do anunciante. Depois de comprar, volte e marque o
                produto como comprado.
              </p>

              <div className="flex justify-center">
                <ShareButton title={product.title} path={path} />
              </div>
            </div>
          </div>
        </div>

        {product.specifications.length > 0 && (
          <section className="mt-8 border-t border-line pt-6">
            <h2 className="text-[17px] font-semibold text-ink lg:text-xl">Especificações do produto</h2>
            <div className="mt-4 grid gap-6 md:grid-cols-2">
              {product.specifications.map((group) => (
                <div key={group.title}>
                  <h3 className="mb-2 text-sm font-semibold text-ink">{group.title}</h3>
                  <dl className="overflow-hidden rounded border border-line">
                    {group.items.map((item, index) => (
                      <div
                        key={item.label}
                        className={cn(
                          'grid grid-cols-2 gap-4 px-4 py-2.5 text-[13px]',
                          index % 2 === 0 ? 'bg-surface-muted' : 'bg-surface',
                        )}
                      >
                        <dt className="text-ink-soft">{item.label}</dt>
                        <dd className="text-ink">{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </section>
        )}

        {(product.description || product.rating != null) && (
          <section className="mt-8 grid gap-8 border-t border-line pt-6 md:grid-cols-[minmax(0,1fr)_260px]">
            {product.description && (
              <div>
                <h2 className="text-[17px] font-semibold text-ink lg:text-xl">Outras informações</h2>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-soft">
                  {product.description}
                </p>
              </div>
            )}

            {product.rating != null && (
              <div>
                <h2 className="text-[17px] font-semibold text-ink lg:text-xl">Avaliação do produto</h2>
                <div className="mt-3 flex items-center gap-3">
                  <span className="text-[28px] font-semibold text-ink lg:text-4xl">
                    {product.rating.toFixed(1)}
                  </span>
                  <Rating value={product.rating} size={20} />
                </div>
                {product.ratingCount != null && product.ratingCount > 0 && (
                  <p className="mt-1.5 text-[13px] text-ink-faint">
                    {product.ratingCount} avaliações
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        <p className="sr-only">Preço atual: {formatCurrency(product.price)}</p>
      </Card>
    </div>
  )
}

/* ------------------------------------------------------------------ */

interface ThumbnailsProps {
  images: string[]
  title: string
  active: number
  onSelect: (index: number) => void
}

function Thumbnails({ images, title, active, onSelect }: ThumbnailsProps) {
  if (images.length <= 1) return <div className="hidden sm:block" />

  return (
    <div className="order-2 flex gap-2 sm:order-none sm:flex-col">
      {images.map((image, index) => (
        <button
          key={image}
          type="button"
          onMouseEnter={() => onSelect(index)}
          onFocus={() => onSelect(index)}
          onClick={() => onSelect(index)}
          aria-label={`Ver imagem ${index + 1} de ${title}`}
          aria-current={index === active}
          className={cn(
            'size-14 shrink-0 overflow-hidden rounded border transition-colors',
            index === active ? 'border-brand' : 'border-line hover:border-ink-faint',
          )}
        >
          <img src={image} alt="" className="size-full object-contain" />
        </button>
      ))}
    </div>
  )
}
